import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramHoodFaceVelocity } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.hoodFaceVelocity.v1';

type Fields = {
  cfm: string;
  widthIn: string;
  heightIn: string;
  areaFt2: string;
  velocityFpm: string;
};
const EMPTY: Fields = { cfm: '', widthIn: '', heightIn: '', areaFt2: '', velocityFpm: '' };

/**
 * Hood Face Velocity — V = Q / A.
 * Width/Height (in diameter units) optionally compute area when area is blank.
 */
const AirflowHoodFaceVelocityScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = [fields.cfm !== '', fields.areaFt2 !== '', fields.velocityFpm !== ''].filter(Boolean).length;
    if (filled < 2) {
      setError('Fill at least two of Flow, Area, or Velocity to calculate the missing value.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    // area in ft² internally
    let areaFt2 = fields.areaFt2 !== '' ? toImp(fields.areaFt2, 'area') : NaN;
    if (fields.areaFt2 === '' && fields.widthIn !== '' && fields.heightIn !== '') {
      const wIn = toImp(fields.widthIn, 'diameter');
      const hIn = toImp(fields.heightIn, 'diameter');
      if (wIn <= 0 || hIn <= 0) {
        setError('Width and Height must be > 0.');
        return;
      }
      areaFt2 = (wIn * hIn) / 144;
      set({ areaFt2: fromImp(areaFt2, 'area', 3) });
    }

    const q = fields.cfm !== '' ? toImp(fields.cfm, 'flow') : NaN;
    const v = fields.velocityFpm !== '' ? toImp(fields.velocityFpm, 'velocity') : NaN;

    if (fields.areaFt2 !== '' && (!Number.isFinite(areaFt2) || areaFt2 <= 0)) {
      setError('Area must be > 0.');
      return;
    }

    if (fields.velocityFpm === '') {
      if (!Number.isFinite(q) || !Number.isFinite(areaFt2) || areaFt2 === 0) {
        setError('Need Flow and Area to calculate Velocity.');
        return;
      }
      setSolvedFor('velocityFpm');
      set({ velocityFpm: fromImp(q / areaFt2, 'velocity', 0) });
    } else if (fields.cfm === '') {
      if (!Number.isFinite(v) || !Number.isFinite(areaFt2)) {
        setError('Need Velocity and Area to calculate Flow.');
        return;
      }
      setSolvedFor('cfm');
      set({ cfm: fromImp(v * areaFt2, 'flow', 0) });
    } else if (fields.areaFt2 === '') {
      if (!Number.isFinite(q) || !Number.isFinite(v) || v === 0) {
        setError('Need Flow and Velocity to calculate Area.');
        return;
      }
      setSolvedFor('areaFt2');
      set({ areaFt2: fromImp(q / v, 'area', 3) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.cfm && { label: 'Flow', value: fields.cfm, unit: unit('flow'), emphasis: solvedFor === 'cfm' },
    fields.widthIn && { label: 'Width', value: fields.widthIn, unit: unit('diameter') },
    fields.heightIn && { label: 'Height', value: fields.heightIn, unit: unit('diameter') },
    fields.areaFt2 && { label: 'Area', value: fields.areaFt2, unit: unit('area'), emphasis: solvedFor === 'areaFt2' },
    fields.velocityFpm && { label: 'Face Velocity', value: fields.velocityFpm, unit: unit('velocity'), emphasis: solvedFor === 'velocityFpm' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Hood Face Velocity"
      subtitle="Face velocity uses V = Q / A. Enter opening size to compute area."
      formula={
        <>
          <FormulaLine>V ({unit('velocity')}) = Q ({unit('flow')}) / A ({unit('area')})</FormulaLine>
          <FormulaNote>A = Width × Height / {isImperial ? '144' : '1,000,000'} (if entering in {unit('diameter')})</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: V = Q / A"
      diagram={<DiagramHoodFaceVelocity />}
      outputSlots={{
        cfm: fields.cfm ? toImp(fields.cfm, 'flow') : undefined,
        area_sqft: fields.areaFt2 ? toImp(fields.areaFt2, 'area') : undefined,
        velocity_fpm: fields.velocityFpm ? toImp(fields.velocityFpm, 'velocity') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.cfm) {
          patch.cfm = fromImp(values.cfm, 'flow', 0);
        }
        if (values.area_sqft != null && !fields.areaFt2) {
          patch.areaFt2 = fromImp(values.area_sqft, 'area', 3);
        }
        if (values.velocity_fpm != null && !fields.velocityFpm) {
          patch.velocityFpm = fromImp(values.velocity_fpm, 'velocity', 0);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Flow (${unit('flow')})`}
          value={fields.cfm}
          onChangeText={(v) => set({ cfm: v })}
          rightText={unit('flow')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Width (${unit('diameter')})`}
          value={fields.widthIn}
          onChangeText={(v) => set({ widthIn: v })}
          rightText={unit('diameter')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Height (${unit('diameter')})`}
          value={fields.heightIn}
          onChangeText={(v) => set({ heightIn: v })}
          rightText={unit('diameter')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Area (${unit('area')})`}
          value={fields.areaFt2}
          onChangeText={(v) => set({ areaFt2: v })}
          rightText={unit('area')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Face Velocity (${unit('velocity')})`}
          value={fields.velocityFpm}
          onChangeText={(v) => set({ velocityFpm: v })}
          rightText={unit('velocity')}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowHoodFaceVelocityScreen;
